import { useEffect, useMemo, useRef, useState } from "react";

// Clean vertical workflow visualization. Orthogonal connectors. Numbered
// agents grouped by stage. Click any agent → detail in side panel.
// "Play incident" animates a packet through every edge in flow order, with
// the parallel triage block firing simultaneously.

type StageKey = "intake" | "triage" | "response" | "hitl" | "execution" | "audit_resolution";

type AgentId =
  | "00"
  | "01" | "02" | "03" | "04" | "05" | "06"
  | "07"
  | "08" | "human"
  | "09"
  | "10" | "11";

interface AgentInfo {
  id: AgentId;
  glyph: string;           // visual icon — e.g. "</>", "NET", "Σ", "👤"
  name: string;            // "Triage Coordinator"
  stage: StageKey;
  status: "Build" | "Reference" | "Human" | "Intake";
  blurb: string;
  ioIn: string;
  ioOut: string;
  specPath?: string;
  skillPath?: string;
}

const AGENTS: AgentInfo[] = [
  { id: "00",    glyph: "⤓",   name: "Incident Intake",       stage: "intake",            status: "Intake",
    blurb: "Receives webhook events from SIEM, EDR, DLP, CSPM, WAF, AI Gateway, or user reports. Validates against the canonical incident schema and hands off to the triage coordinator.",
    ioIn: "Source-specific event payload", ioOut: "IncidentInput" },
  { id: "01",    glyph: "✦",   name: "Triage Coordinator",    stage: "triage",            status: "Build",
    blurb: "Pure orchestration. Inspects the incident, decides which sub-agents to invoke (skipping ones for which the relevant data isn't present), dispatches them in parallel via service bindings.",
    ioIn: "IncidentInput", ioOut: "Per-agent dispatch + bundled findings",
    specPath: "agents/01-triage-coordinator.md", skillPath: "skills/triage-coordinator.skill.md" },
  { id: "02",    glyph: "</>", name: "Command-Line Analyzer", stage: "triage",            status: "Build",
    blurb: "Decodes base64/PowerShell payloads, identifies LOLBin abuse, flags suspicious arguments, extracts URLs/IPs/domains from decoded commands.",
    ioIn: "{ command_line, parent_process }", ioOut: "CommandFindings",
    specPath: "agents/02-command-line-analyzer.md", skillPath: "skills/command-line-analyzer.skill.md" },
  { id: "03",    glyph: "ID",  name: "Identity Analyzer",     stage: "triage",            status: "Build",
    blurb: "Assesses anomaly score from auth signals, computes user risk factors (privilege, departure window, sensitive access), produces a compromise likelihood.",
    ioIn: "{ user_email, anomaly_signals, user_metadata }", ioOut: "IdentityFindings",
    specPath: "agents/03-identity-analyzer.md", skillPath: "skills/identity-analyzer.skill.md" },
  { id: "04",    glyph: "NET", name: "Network Analyzer",      stage: "triage",            status: "Build",
    blurb: "Scores destination reputation, identifies infrastructure anomalies (ASN, hosting age, cert age), produces a verdict on the network indicator.",
    ioIn: "{ destination_ip, destination_domain, bytes, process }", ioOut: "NetworkFindings",
    specPath: "agents/04-network-analyzer.md", skillPath: "skills/network-analyzer.skill.md" },
  { id: "05",    glyph: "ACT", name: "Activity Analyzer",     stage: "triage",            status: "Reference",
    blurb: "Optional fourth sub-agent. Pulls recent user + host activity logs, identifies baseline deviations, surfaces concerning patterns, checks cross-incident correlations.",
    ioIn: "{ user_email, hostname, activity_context, baseline }", ioOut: "ActivityFindings",
    specPath: "agents/05-activity-analyzer.md", skillPath: "skills/activity-analyzer.skill.md" },
  { id: "06",    glyph: "Σ",   name: "Triage Synthesizer",    stage: "triage",            status: "Build",
    blurb: "Takes the bundled sub-agent findings and produces a structured triage card with severity, summary, indicators, confidence, and sub-agent agreement.",
    ioIn: "CoordinatorOutput", ioOut: "TriageCard",
    specPath: "agents/06-triage-synthesizer.md", skillPath: "skills/triage-synthesizer.skill.md" },
  { id: "07",    glyph: "▶",   name: "Response Agent",        stage: "response",          status: "Build",
    blurb: "Matches the incident type to relevant playbooks, extracts specific actions tailored to the indicators, marks each with risk + reversibility + prerequisites + rationale.",
    ioIn: "TriageCard", ioOut: "ActionPlan { matched_playbooks, actions[] }",
    specPath: "agents/07-response-agent.md", skillPath: "skills/response-agent.skill.md" },
  { id: "08",    glyph: "✉",   name: "Notification Agent",    stage: "hitl",              status: "Reference",
    blurb: "Routes the triage card + action plan to the on-call analyst's primary surface (Chat by default, configurable per deployment).",
    ioIn: "TriageCard + ActionPlan", ioOut: "HITL card surfaced to analyst",
    specPath: "agents/08-notification-agent.md", skillPath: "skills/notification-agent.skill.md" },
  { id: "human", glyph: "👤",  name: "Human Analyst",         stage: "hitl",              status: "Human",
    blurb: "Per-action approve / reject / escalate. Sees risk + reversibility + rationale + audit context. The hard architectural boundary: no destructive action proceeds without a named human decision.",
    ioIn: "HITL card", ioOut: "ApprovalEvent { decisions[] }" },
  { id: "09",    glyph: "⇉",   name: "Action Dispatcher",     stage: "execution",         status: "Reference",
    blurb: "Receives the approved action set and fans out to integration handlers (EDR, IAM, Gateway, Chat, Backup, etc.). Respects prerequisites, retries on transient failures.",
    ioIn: "ApprovalEvent (approved actions only)", ioOut: "Per-action execution results",
    specPath: "agents/09-action-dispatcher.md", skillPath: "skills/action-dispatcher.skill.md" },
  { id: "10",    glyph: "✎",   name: "Audit Writer",          stage: "audit_resolution",  status: "Reference",
    blurb: "Writes every step of the harness — every prompt, every model response, every decision, every dispatch result — to R2 in an append-only audit transcript.",
    ioIn: "All upstream events", ioOut: "R2 audit transcript",
    specPath: "agents/10-audit-writer.md", skillPath: "skills/audit-writer.skill.md" },
  { id: "11",    glyph: "✓",   name: "Resolution Summarizer", stage: "audit_resolution",  status: "Reference",
    blurb: "Drafts the post-incident summary: narrative, timeline, actions taken, lessons learned. Links to the Jira ticket, the audit transcript, and any related incidents.",
    ioIn: "Full session state", ioOut: "Resolution summary",
    specPath: "agents/11-resolution-summarizer.md", skillPath: "skills/resolution-summarizer.skill.md" },
];

// Stage colors — every box in a stage shares the same accent so the lanes
// visually group themselves. Colors are official Cloudflare brand palette.
const STAGE_COLOR: Record<StageKey, string> = {
  intake:            "#3E74FF", // Blueberry
  triage:            "#F6821F", // Tangerine
  response:          "#FBAD41", // Mango
  hitl:              "#941FBA", // Purple
  execution:         "#FF6633", // Ruby
  audit_resolution:  "#747474", // Gray (terminal stages)
};

// --- Layout ------------------------------------------------------------------
// Canvas dimensions
const W = 1080;
const H = 1320;
const COL = [120, 320, 540, 760, 960]; // 5 columns, centered around 540
const ROW = {
  intake: 70,
  coord: 220,
  sub: 400,
  synth: 580,
  response: 750,
  hitl: 920,
  dispatch: 1080,
  audit: 1240,
};

const BOX = { w: 180, h: 86 };

// Stage label band Y ranges (for the muted lane labels on the left)
const STAGE_BANDS: Array<{ key: StageKey; label: string; y0: number; y1: number }> = [
  { key: "intake",            label: "INTAKE",              y0: 24,   y1: 156 },
  { key: "triage",            label: "TRIAGE",              y0: 160,  y1: 666 },
  { key: "response",          label: "RESPONSE",            y0: 670,  y1: 836 },
  { key: "hitl",              label: "HUMAN-IN-THE-LOOP",   y0: 840,  y1: 1006 },
  { key: "execution",         label: "EXECUTION",           y0: 1010, y1: 1166 },
  { key: "audit_resolution",  label: "AUDIT & RESOLUTION",  y0: 1170, y1: 1312 },
];

interface AgentPos { x: number; y: number; }
const POS: Record<AgentId, AgentPos> = {
  "00":    { x: COL[2], y: ROW.intake },
  "01":    { x: COL[2], y: ROW.coord },
  "02":    { x: COL[0], y: ROW.sub },
  "03":    { x: COL[1], y: ROW.sub },
  "04":    { x: COL[3], y: ROW.sub },
  "05":    { x: COL[4], y: ROW.sub },
  "06":    { x: COL[2], y: ROW.synth },
  "07":    { x: COL[2], y: ROW.response },
  "08":    { x: COL[1], y: ROW.hitl },
  human:   { x: COL[3], y: ROW.hitl },
  "09":    { x: COL[2], y: ROW.dispatch },
  "10":    { x: COL[1], y: ROW.audit },
  "11":    { x: COL[3], y: ROW.audit },
};

// Edges: each edge is (from, to, payload-label, group-id for play animation).
// Group ID lets us light up parallel edges simultaneously during play.
type EdgeGroup =
  | "intake_to_coord"
  | "coord_to_subagents"
  | "subagents_to_synth"
  | "synth_to_response"
  | "response_to_notify"
  | "notify_to_human"
  | "human_to_dispatch"
  | "dispatch_to_audit"
  | "dispatch_to_resolution";

interface Edge {
  from: AgentId;
  to: AgentId;
  label: string;
  group: EdgeGroup;
}

const EDGES: Edge[] = [
  { from: "00",    to: "01",    label: "IncidentInput",     group: "intake_to_coord" },
  { from: "01",    to: "02",    label: "scoped command",    group: "coord_to_subagents" },
  { from: "01",    to: "03",    label: "scoped identity",   group: "coord_to_subagents" },
  { from: "01",    to: "04",    label: "scoped network",    group: "coord_to_subagents" },
  { from: "01",    to: "05",    label: "scoped activity",   group: "coord_to_subagents" },
  { from: "02",    to: "06",    label: "CommandFindings",   group: "subagents_to_synth" },
  { from: "03",    to: "06",    label: "IdentityFindings",  group: "subagents_to_synth" },
  { from: "04",    to: "06",    label: "NetworkFindings",   group: "subagents_to_synth" },
  { from: "05",    to: "06",    label: "ActivityFindings",  group: "subagents_to_synth" },
  { from: "06",    to: "07",    label: "TriageCard",        group: "synth_to_response" },
  { from: "07",    to: "08",    label: "ActionPlan",        group: "response_to_notify" },
  { from: "08",    to: "human", label: "HITL card",         group: "notify_to_human" },
  { from: "human", to: "09",    label: "ApprovalEvent",     group: "human_to_dispatch" },
  { from: "09",    to: "10",    label: "execution_results", group: "dispatch_to_audit" },
  { from: "10",    to: "11",    label: "session_state",     group: "dispatch_to_resolution" },
];

const PLAY_ORDER: EdgeGroup[] = [
  "intake_to_coord",
  "coord_to_subagents",
  "subagents_to_synth",
  "synth_to_response",
  "response_to_notify",
  "notify_to_human",
  "human_to_dispatch",
  "dispatch_to_audit",
  "dispatch_to_resolution",
];

const GROUP_DURATION_MS: Record<EdgeGroup, number> = {
  intake_to_coord:        700,
  coord_to_subagents:     900,
  subagents_to_synth:     900,
  synth_to_response:      700,
  response_to_notify:     700,
  notify_to_human:        700,
  human_to_dispatch:      900, // longer — the human decision pause
  dispatch_to_audit:      700,
  dispatch_to_resolution: 700,
};

// --- Orthogonal pathing ------------------------------------------------------
// Build a clean L-shaped path between two boxes. We exit the source from its
// bottom or side, do a horizontal segment at a "bus" Y, and approach the target
// from above. Returns an SVG path "d" string plus a midpoint for labels.

function orthPath(from: AgentPos, to: AgentPos): { d: string; mid: AgentPos } {
  const fromBottom = { x: from.x, y: from.y + BOX.h / 2 };
  const toTop      = { x: to.x,   y: to.y   - BOX.h / 2 };

  // Same column? Straight vertical line.
  if (from.x === to.x) {
    const d = `M ${fromBottom.x} ${fromBottom.y} L ${toTop.x} ${toTop.y}`;
    return { d, mid: { x: from.x, y: (fromBottom.y + toTop.y) / 2 } };
  }

  // Different columns: route via a horizontal bus midway between source and target Y.
  const busY = (fromBottom.y + toTop.y) / 2;
  // 8px rounded corners
  const r = 8;
  const goingRight = to.x > from.x;
  const cornerOffset = goingRight ? r : -r;

  const d = [
    `M ${fromBottom.x} ${fromBottom.y}`,
    `L ${fromBottom.x} ${busY - r}`,
    `Q ${fromBottom.x} ${busY} ${fromBottom.x + cornerOffset} ${busY}`,
    `L ${toTop.x - cornerOffset} ${busY}`,
    `Q ${toTop.x} ${busY} ${toTop.x} ${busY + r}`,
    `L ${toTop.x} ${toTop.y}`,
  ].join(" ");

  return { d, mid: { x: (fromBottom.x + toTop.x) / 2, y: busY } };
}

// Compute path data for every edge once
const EDGE_PATHS = EDGES.map((edge) => {
  const from = POS[edge.from];
  const to = POS[edge.to];
  return { ...edge, ...orthPath(from, to) };
});

// --- Component ---------------------------------------------------------------

export function Workflow() {
  const [selected, setSelected] = useState<AgentId | null>("01");
  const [activeGroup, setActiveGroup] = useState<EdgeGroup | null>(null);
  const [reached, setReached] = useState<Set<AgentId>>(new Set(["00"]));
  const [playing, setPlaying] = useState(false);
  const playRef = useRef(false);

  const selectedInfo = useMemo(
    () => AGENTS.find((a) => a.id === selected) ?? null,
    [selected],
  );

  const play = async () => {
    if (playRef.current) return;
    playRef.current = true;
    setPlaying(true);
    setReached(new Set(["00"]));
    setActiveGroup(null);

    for (const group of PLAY_ORDER) {
      setActiveGroup(group);
      const newReached: AgentId[] = [];
      for (const edge of EDGES) {
        if (edge.group === group) newReached.push(edge.to);
      }
      setReached((prev) => new Set([...prev, ...newReached]));
      await new Promise((r) => setTimeout(r, GROUP_DURATION_MS[group]));
    }

    setActiveGroup(null);
    setPlaying(false);
    playRef.current = false;
  };

  const reset = () => {
    setActiveGroup(null);
    setReached(new Set(["00"]));
    setSelected(null);
  };

  useEffect(() => () => { playRef.current = false; }, []);

  return (
    <div className="wf">
      <div className="wf-header">
        <div>
          <h1>SIRT Agentic Workflow</h1>
          <p className="muted">
            The 11-agent harness end-to-end. Click any agent for details.{" "}
            <strong>Play</strong> animates an incident flowing through every
            stage — parallel triage sub-agents fire simultaneously, HITL pauses,
            audit + resolution close it out.
          </p>
        </div>
        <div className="wf-controls">
          <button type="button" className="button" onClick={reset} disabled={playing}>
            Reset
          </button>
          <button
            type="button"
            className="button primary lg"
            onClick={play}
            disabled={playing}
          >
            {playing ? "Running…" : "▶ Play incident"}
          </button>
        </div>
      </div>

      <div className="wf-grid">
        <div className="wf-canvas">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMin meet"
            className="wf-svg"
            role="img"
            aria-label="Agentic SIRT workflow"
          >
            <defs>
              <marker
                id="arrow-static"
                viewBox="0 0 12 12"
                refX="10"
                refY="6"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 12 6 L 0 12 z" fill="#a0a0a8" />
              </marker>
              <marker
                id="arrow-active"
                viewBox="0 0 12 12"
                refX="10"
                refY="6"
                markerWidth="8"
                markerHeight="8"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 12 6 L 0 12 z" fill="#F6821F" />
              </marker>
              <filter id="card-shadow" x="-10%" y="-10%" width="120%" height="120%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08" />
              </filter>
            </defs>

            {/* Stage bands — subtle horizontal zones */}
            {STAGE_BANDS.map((band) => (
              <g key={band.key}>
                <line
                  x1="60"
                  y1={band.y0}
                  x2={W - 20}
                  y2={band.y0}
                  stroke="#e6e6ea"
                  strokeWidth="1"
                />
                <text
                  x="60"
                  y={band.y0 + 18}
                  fontSize="10"
                  fontWeight="700"
                  letterSpacing="2"
                  fill={band.key === "hitl" ? "#F6821F" : "#8a8a95"}
                >
                  {band.label}
                </text>
              </g>
            ))}

            {/* Subtle HITL band background */}
            <rect
              x="60"
              y={STAGE_BANDS.find((b) => b.key === "hitl")!.y0}
              width={W - 80}
              height={
                STAGE_BANDS.find((b) => b.key === "hitl")!.y1 -
                STAGE_BANDS.find((b) => b.key === "hitl")!.y0
              }
              fill="#F6821F"
              fillOpacity="0.025"
            />

            {/* Edges (drawn before agents so boxes overlap them) */}
            {EDGE_PATHS.map((edge, idx) => {
              const active = activeGroup === edge.group;
              return (
                <EdgeView
                  key={`${edge.from}-${edge.to}-${idx}`}
                  edge={edge}
                  active={active}
                />
              );
            })}

            {/* Agent boxes */}
            {AGENTS.map((agent) => {
              const pos = POS[agent.id];
              return (
                <AgentNode
                  key={agent.id}
                  agent={agent}
                  x={pos.x}
                  y={pos.y}
                  reached={reached.has(agent.id)}
                  selected={selected === agent.id}
                  onClick={() => setSelected(agent.id === selected ? null : agent.id)}
                />
              );
            })}
          </svg>
        </div>

        <aside className="wf-side">
          {!selectedInfo ? (
            <EmptySide />
          ) : (
            <AgentDetail info={selectedInfo} />
          )}
        </aside>
      </div>
    </div>
  );
}

// ----- Edge component --------------------------------------------------------

function EdgeView({
  edge,
  active,
}: {
  edge: { d: string; mid: AgentPos; label: string; group: EdgeGroup };
  active: boolean;
}) {
  return (
    <g className={active ? "wf-edge active" : "wf-edge"}>
      {/* Base path always rendered */}
      <path
        d={edge.d}
        fill="none"
        stroke={active ? "#F6821F" : "#c9c9d1"}
        strokeWidth={active ? 2.25 : 1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd={active ? "url(#arrow-active)" : "url(#arrow-static)"}
        opacity={active ? 1 : 0.6}
      />
      {/* Animated packet riding the line */}
      {active && (
        <>
          <circle r="5" fill="#F6821F">
            <animateMotion dur="0.6s" repeatCount="1" path={edge.d} />
          </circle>
          <g transform={`translate(${edge.mid.x + 8}, ${edge.mid.y - 8})`}>
            <rect x="0" y="-12" rx="3" ry="3" width={edge.label.length * 6 + 12} height="18" fill="#fff" stroke="#F6821F" strokeWidth="1" />
            <text x="6" y="2" fontSize="11" fill="#F6821F" fontWeight="600">
              {edge.label}
            </text>
          </g>
        </>
      )}
    </g>
  );
}

// ----- Agent node ------------------------------------------------------------

function AgentNode({
  agent,
  x,
  y,
  reached,
  selected,
  onClick,
}: {
  agent: AgentInfo;
  x: number;
  y: number;
  reached: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  const w = BOX.w;
  const h = BOX.h;

  // Every box looks the same structurally; color comes from the stage.
  // Human Analyst keeps a subtle warm fill to mark it as the HITL gate.
  const stageColor = STAGE_COLOR[agent.stage];
  const fill = agent.status === "Human" ? "#FFF6E8" : "#ffffff";
  const stroke = stageColor;
  const text = "#1d1d1f";
  const badgeFill = stageColor;
  const badgeText = "#fff";

  // Selected emphasis
  const finalStroke = selected ? "#1d1d1f" : stroke;
  const finalStrokeWidth = selected ? 2.4 : 1.6;
  const opacity = reached ? 1 : 0.72;

  return (
    <g
      transform={`translate(${x - w / 2}, ${y - h / 2})`}
      className="wf-node"
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      {/* Drop shadow card */}
      <rect
        x="0"
        y="0"
        width={w}
        height={h}
        rx="10"
        fill={fill}
        stroke={finalStroke}
        strokeWidth={finalStrokeWidth}
        opacity={opacity}
        filter="url(#card-shadow)"
        style={{ transition: "opacity 0.3s, stroke-width 0.15s" }}
      />

      {/* Agent glyph badge */}
      <rect x="10" y="10" width="34" height="22" rx="6" fill={badgeFill} />
      <text
        x="27"
        y={agent.glyph.length > 1 ? 25 : 26}
        textAnchor="middle"
        fontSize={agent.glyph.length === 1 ? 14 : 11}
        fontWeight="700"
        fill={badgeText}
        letterSpacing={agent.glyph.length > 1 ? "0.4" : "0"}
      >
        {agent.glyph}
      </text>

      {/* Agent name */}
      <text
        x={w / 2}
        y={h - 26}
        textAnchor="middle"
        fontSize="14"
        fontWeight="600"
        fill={text}
      >
        {agent.name}
      </text>

      {/* Stage subline */}
      <text
        x={w / 2}
        y={h - 12}
        textAnchor="middle"
        fontSize="10"
        fill="#8a8a95"
        letterSpacing="0.3"
      >
        {stageLabel(agent.stage)}
      </text>
    </g>
  );
}

// ----- Side panel ------------------------------------------------------------

function EmptySide() {
  return (
    <div className="wf-side-empty">
      <h3>Click any agent</h3>
      <p className="muted">
        Each box is a Cloudflare Worker (or in the case of <strong>Human Analyst</strong>,
        a human action gate). Click one to see what it does, what it takes as input,
        and what it produces as output.
      </p>

      <div className="wf-legend">
        <div className="wf-legend-title">Stages</div>
        <LegendRow color={STAGE_COLOR.intake}            label="Intake"               desc="Receives the incident webhook" />
        <LegendRow color={STAGE_COLOR.triage}            label="Triage"               desc="Coordinator + sub-agents + synthesizer" />
        <LegendRow color={STAGE_COLOR.response}          label="Response"             desc="Matches playbooks, drafts the action plan" />
        <LegendRow color={STAGE_COLOR.hitl}              label="Human-in-the-loop"    desc="Notifies + the analyst decides" />
        <LegendRow color={STAGE_COLOR.execution}         label="Execution"            desc="Approved actions fan out to integrations" />
        <LegendRow color={STAGE_COLOR.audit_resolution}  label="Audit & resolution"   desc="Writes the transcript, drafts the summary" />
      </div>
    </div>
  );
}

function LegendRow({
  color,
  label,
  desc,
}: {
  color: string;
  label: string;
  desc: string;
}) {
  return (
    <div className="legend-row">
      <span
        className="legend-swatch"
        style={{ background: color }}
      />
      <span>
        <strong>{label}</strong> — {desc}
      </span>
    </div>
  );
}

function AgentDetail({ info }: { info: AgentInfo }) {
  return (
    <div className="agent-detail">
      <div className="agent-detail-head">
        <div
          className="agent-detail-id"
          style={{
            background: STAGE_COLOR[info.stage],
            fontSize: info.glyph.length === 1 ? "18px" : "13px",
          }}
        >
          {info.glyph}
        </div>
        <div>
          <div className="agent-detail-label">{info.name}</div>
          <div className="agent-detail-stage muted">{stageLabel(info.stage)}</div>
        </div>
      </div>

      <p className="agent-detail-blurb">{info.blurb}</p>

      <div className="agent-detail-io">
        <div>
          <div className="agent-detail-iolabel">Input</div>
          <div className="agent-detail-iovalue mono">{info.ioIn}</div>
        </div>
        <div>
          <div className="agent-detail-iolabel">Output</div>
          <div className="agent-detail-iovalue mono">{info.ioOut}</div>
        </div>
      </div>

      {(info.specPath || info.skillPath) && (
        <div className="agent-detail-spec muted">
          {info.specPath && (
            <>
              Spec: <span className="mono">{info.specPath}</span>
              <br />
            </>
          )}
          {info.skillPath && (
            <>
              Skill: <span className="mono">{info.skillPath}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function stageLabel(s: StageKey): string {
  switch (s) {
    case "intake":           return "Intake";
    case "triage":           return "Triage";
    case "response":         return "Response";
    case "hitl":             return "Human-in-the-loop";
    case "execution":        return "Execution";
    case "audit_resolution": return "Audit & Resolution";
  }
}
